import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { IconTruckDelivery } from '@tabler/icons-react';

import { ABORTED, DELIVERED, INPROGRESS, NOT_DELIVERED } from 'constants/states-app';

import { colors } from 'styles/theme';
import { GetArchivedOrdersQuery, Maybe, OrderState, Scalars } from 'data/__generated__';

interface OrderTableStateProps {
    delivery?: GetArchivedOrdersQuery['archivedOrders']['archivedOrders'][number]['delivery'] | null | undefined;
    firstOrder?: Maybe<Scalars['Boolean']['output']> | null;
    isPunctual: boolean;
    // state: GetArchivedOrdersQuery['archivedOrders']['archivedOrders'][number]['state'] | '';
    state: OrderState | 'Created';
    hasBeenValidated: boolean;
}
export const OrderTableState: React.FC<OrderTableStateProps> = ({
    delivery,
    firstOrder,
    hasBeenValidated,
    isPunctual,
    state,
}) => {
    const isUnvalidatedFirstOrder = firstOrder && !hasBeenValidated;
    const hasBeenDelivered = delivery?.state === DELIVERED;
    const isBeingDelivered = delivery?.state === INPROGRESS;
    const hasBeenAborted =
        delivery?.stateHistory.some(({ state }) => state === ABORTED) || delivery?.state === NOT_DELIVERED;

    const statusInfo = {
        Created:
            isPunctual || isUnvalidatedFirstOrder
                ? {
                      status: 'En cours de création',
                      color: colors.orange,
                  }
                : { status: 'Automatiquement renouvelée', color: colors.green },
        InProgress: isPunctual
            ? {
                  status: 'En cours de création',
                  color: colors.orange,
              }
            : isUnvalidatedFirstOrder
              ? {
                    status: 'En cours de création',
                    color: colors.orange,
                }
              : {
                    status: 'En cours de modification',
                    color: colors.yellow,
                },
        Validated: {
            status: 'Validée',
            color: colors.green,
        },
        Ready: hasBeenDelivered
            ? { status: 'Livrée', color: colors.green }
            : isBeingDelivered
              ? { status: 'Livraison en cours', color: colors.blue }
              : !isPunctual && (!delivery || hasBeenAborted)
                ? // Ready recurring orders should always have an associated delivery (otherwise the order is cancelled)
                  {
                      status: 'Annulée',
                      color: colors.red,
                  }
                : { status: 'En préparation', color: colors.green },
        Archived:
            !delivery || hasBeenAborted
                ? {
                      status: 'Annulée',
                      color: colors.red,
                  }
                : { status: 'Livrée', color: colors.green },
        Paused: { status: 'Mise en pause', color: colors.pantinGrey },
        Deleted: { status: 'Supprimée', color: colors.pantinGrey },
    };
    const { status, color } = statusInfo[state];
    const trackingHref = getTrackingHref(delivery);

    return (
        <StatusCell data-test="order-status">
            <TotemStatus color={color} />
            {delivery?.state === INPROGRESS && trackingHref ? (
                trackingHref.isInternal ? (
                    <InternalLinkToDelivery to={trackingHref.url} title="Voir la livraison en cours">
                        {status}
                        <IconTruckDeliveryAsLink size="18px" />
                    </InternalLinkToDelivery>
                ) : (
                    <LinkToDelivery
                        href={trackingHref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Voir la livraison en cours"
                    >
                        {status}
                        <IconTruckDeliveryAsLink size="18px" />
                    </LinkToDelivery>
                )
            ) : (
                status
            )}
        </StatusCell>
    );
};

/**
 * Qui affiche le suivi : le transporteur ou nous. Tant qu'Urbantz pilote la tournée il fournit un
 * `trackingUrl`, et c'est SA page qui fait foi — elle en sait plus que la nôtre (position du camion).
 * Une livraison sans lien (planifiée en interne, ou Urbantz coupé) bascule sur la page maison, sans
 * rien à basculer : la donnée décide. `TRACKING_PAGE` force la page maison pour tester avant la coupure.
 */
function getTrackingHref(delivery: OrderTableStateProps['delivery']): { url: string; isInternal: boolean } | null {
    if (!delivery) {
        return null;
    }

    if (delivery.trackingUrl && !window.__TOTENV__?.TRACKING_PAGE) {
        return { url: delivery.trackingUrl, isInternal: false };
    }

    return { url: `/suivi/${delivery._id}`, isInternal: true };
}

const linkToDeliveryStyle = css`
    display: flex;
    align-items: center;
`;

const InternalLinkToDelivery = styled(Link)`
    ${linkToDeliveryStyle}
`;

const LinkToDelivery = styled.a`
    display: flex;
    align-items: center;
`;

const IconTruckDeliveryAsLink = styled(IconTruckDelivery)`
    margin-left: 5px;
`;

const StatusCell = styled.div`
    display: flex;
    align-items: center;
`;

const TotemStatus = styled.div`
    background-color: ${({ color }) => color};
    border-radius: 7px;
    height: 14px;
    width: 14px;
    margin-right: 7px;
`;
